import { cn } from '../../lib/cn.ts';
import { formatInt } from '../../lib/format.ts';
import { Tooltip } from '../Tooltip.tsx';

/** A categorical slot of the chart palette (data-viz.md): never a status colour, never the accent. */
export type DataSlot = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

export const DATA_SLOT_BG: Record<DataSlot, string> = {
  1: 'bg-data-1',
  2: 'bg-data-2',
  3: 'bg-data-3',
  4: 'bg-data-4',
  5: 'bg-data-5',
  6: 'bg-data-6',
  7: 'bg-data-7',
  8: 'bg-data-8',
};

export interface PulseSeries {
  key: string;
  label: string;
  slot: DataSlot;
}

export interface PulseChartProps {
  series: readonly PulseSeries[];
  /** Oldest first; each bucket holds one count per series, in series order. */
  buckets: readonly (readonly number[])[];
  /** The tooltip heading for bucket i: "4:12 to 4:14". */
  bucketLabel: (i: number) => string;
  /** Under the first and the last bar: "60m ago", "Now". */
  startLabel: string;
  endLabel: string;
  /** Accessible summary: "Activity in the last hour: 42 events". */
  label: string;
  /** The smallest top of the scale, so a quiet window does not draw one event as a full bar. */
  minScale?: number;
  className?: string;
}

/**
 * The pulse: events over a recent window as stacked bars, one colour per kind, with a legend of
 * totals. Bars grow from the baseline once when they first appear, and later changes ease their
 * height; under reduced motion they simply appear (the motion tokens go to 0 ms).
 */
export function PulseChart({ series, buckets, bucketLabel, startLabel, endLabel, label, minScale = 4, className }: PulseChartProps) {
  const totals = buckets.map((b) => b.reduce((a, n) => a + n, 0));
  const max = Math.max(minScale, ...totals);
  const seriesTotals = series.map((_, k) => buckets.reduce((a, b) => a + (b[k] ?? 0), 0));
  return (
    <figure className={cn('flex min-w-0 flex-col gap-3', className)}>
      <ul className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1" aria-label="Legend">
        {series.map((s, k) => (
          <li key={s.key} className="flex items-center gap-1.5 text-caption text-fg-muted">
            <span aria-hidden className={cn('size-2 rounded-pill', DATA_SLOT_BG[s.slot])} />
            {s.label}
            <span className="text-fg-subtle num">{formatInt(seriesTotals[k] ?? 0)}</span>
          </li>
        ))}
      </ul>
      <div role="img" aria-label={label} className="relative flex h-16 items-end gap-1">
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 border-t border-dashed border-border-subtle" />
        <span aria-hidden className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed border-border-subtle" />
        {buckets.map((b, i) => {
          const total = totals[i] ?? 0;
          return (
            <Tooltip
              key={i}
              side="top"
              content={
                <span className="flex flex-col gap-0.5">
                  <span className="font-medium">{bucketLabel(i)}</span>
                  {total === 0 ? (
                    <span className="text-fg-muted">Nothing happened</span>
                  ) : (
                    series.map((s, k) =>
                      b[k] ? (
                        <span key={s.key} className="flex items-center gap-1.5 text-fg-muted">
                          <span aria-hidden className={cn('size-2 rounded-pill', DATA_SLOT_BG[s.slot])} />
                          {s.label}
                          <span className="ml-auto pl-3 text-fg num">{formatInt(b[k] ?? 0)}</span>
                        </span>
                      ) : null,
                    )
                  )}
                </span>
              }
            >
              <span className="group/bar relative flex h-full min-w-0 flex-1 flex-col justify-end rounded-control transition-colors duration-(--duration-fast) hover:bg-hover">
                {total === 0 ? (
                  <span aria-hidden className="h-0.5 w-full rounded-pill bg-border" />
                ) : (
                  <span
                    aria-hidden
                    className="motion-grow flex w-full flex-col-reverse overflow-hidden rounded-t-control opacity-90 group-hover/bar:opacity-100"
                    style={{ height: `${Math.max(6, (total / max) * 100)}%`, transitionDelay: `${i * 14}ms` }}
                  >
                    {series.map((s, k) => (b[k] ? <span key={s.key} className={cn('min-h-px w-full basis-0', DATA_SLOT_BG[s.slot])} style={{ flexGrow: b[k] }} /> : null))}
                  </span>
                )}
              </span>
            </Tooltip>
          );
        })}
      </div>
      <figcaption className="flex justify-between text-caption text-fg-subtle" aria-hidden>
        <span>{startLabel}</span>
        <span>{endLabel}</span>
      </figcaption>
    </figure>
  );
}
